export function StatusChip({ status }) {
    const cls =
        status === "posted" ? "se-chip se-chip-posted" :
        status === "draft" ? "se-chip se-chip-draft" :
        status === "extracting" ? "se-chip se-chip-indigo" :
        status === "extraction_failed" ? "se-chip se-chip-err" :
        status === "reversed" ? "se-chip se-chip-err" :
        status === "cancelled" ? "se-chip se-chip-err" :
        status === "inter_bank" ? "se-chip se-chip-indigo" :
        status === "gateway_shortfall" ? "se-chip se-chip-plum" :
        status === "mapped" ? "se-chip se-chip-teal" :
        status === "unmatched" ? "se-chip se-chip-draft" :
        status === "pending_bank_disbursement" ? "se-chip se-chip-plum" :
        "se-chip se-chip-draft";
    const label =
        status === "pending_bank_disbursement" ? "Pending bank match" :
        status === "extracting" ? "Reading…" :
        status === "extraction_failed" ? "AI failed" :
        status === "mapped" ? "AI suggested" :
        status === "unmatched" ? "Awaiting map" :
        String(status || "").replace(/_/g, " ");
    return <span className={cls} data-testid={`status-${status}`}>{label}</span>;
}

export function TagPills({ dept, location, directIndirect }) {
    return (
        <div className="flex flex-wrap gap-1">
            {dept ? <span className="se-tag" style={{ background: "var(--se-teal-soft)", color: "var(--se-teal)", borderColor: "var(--se-teal-soft)" }} data-testid="tag-dept">{dept}</span> : null}
            {location ? <span className="se-tag" style={{ background: "var(--se-forest-soft)", color: "var(--se-forest)", borderColor: "var(--se-forest-soft)" }} data-testid="tag-loc">{location}</span> : null}
            {directIndirect ? <span className="se-tag" style={{ background: "var(--se-plum-soft)", color: "var(--se-plum)", borderColor: "var(--se-plum-soft)" }} data-testid="tag-di">{directIndirect}</span> : null}
        </div>
    );
}

export function ConfidenceBadge({ value }) {
    if (value == null) return null;
    const v = Math.round(value * 100);
    const bg = v >= 90 ? "var(--se-posted-bg)" : v >= 70 ? "var(--se-amber-soft)" : "var(--se-err-bg)";
    const fg = v >= 90 ? "var(--se-posted-text)" : v >= 70 ? "var(--se-amber)" : "var(--se-err-text)";
    return (
        <span
            className="text-[10px] font-semibold rounded-sm px-1.5 py-0.5"
            style={{ background: bg, color: fg }}
            data-testid="confidence-badge"
        >
            {v}%
        </span>
    );
}

// Page-specific colour accents — bright gradients per module
const HEADER_ACCENTS = {
    "Purchase invoices":  "linear-gradient(120deg, #f97316 0%, #f43f5e 60%, #ec4899 100%)",
    "Sales invoices":     "linear-gradient(120deg, #10b981 0%, #14b8a6 55%, #06b6d4 100%)",
    "Customer advances":  "linear-gradient(120deg, #8b5cf6 0%, #ec4899 100%)",
    "Bank statements":    "linear-gradient(120deg, #6366f1 0%, #8b5cf6 60%, #ec4899 100%)",
    "Ledgers":            "linear-gradient(120deg, #06b6d4 0%, #3b82f6 60%, #8b5cf6 100%)",
    "Payables":           "linear-gradient(120deg, #f97316 0%, #ef4444 100%)",
    "Receivables":        "linear-gradient(120deg, #10b981 0%, #22c55e 100%)",
    "Expenses":           "linear-gradient(120deg, #ec4899 0%, #a855f7 100%)",
    "Chart of accounts":  "linear-gradient(120deg, #fbbf24 0%, #f97316 100%)",
    "Users & roles":      "linear-gradient(120deg, #6366f1 0%, #ec4899 100%)",
    "Audit log":          "linear-gradient(120deg, #64748b 0%, #1e293b 100%)",
    "Fiat vouchers":      "linear-gradient(120deg, #f59e0b 0%, #ef4444 55%, #ec4899 100%)",
    "Payroll runs":       "linear-gradient(120deg, #14b8a6 0%, #06b6d4 60%, #8b5cf6 100%)",
    "Employees":          "linear-gradient(120deg, #6366f1 0%, #14b8a6 100%)",
    "Fixed assets":       "linear-gradient(120deg, #a855f7 0%, #6366f1 100%)",
    "Monthly P&L":        "linear-gradient(120deg, #10b981 0%, #f59e0b 100%)",
};

export function PageHeader({ title, sub, right }) {
    const accent = HEADER_ACCENTS[title] || "var(--se-brand-grad)";
    return (
        <div className="relative se-rise z-20">
            {/* Bright gradient strip */}
            <div style={{ height: 4, background: accent }} />
            <div
                className="flex items-start justify-between px-4 sm:px-8 pt-5 sm:pt-6 pb-5 sm:pb-6 flex-wrap gap-3 relative"
                style={{
                    borderBottom: "1px solid var(--se-border)",
                    background: "linear-gradient(180deg, var(--se-bg-alt) 0%, transparent 100%)",
                }}
            >
                {/* Bright ambient blob behind the title — wrapped so overflow-hidden
                    clips only the blob, letting overlay UIs (dropdowns) in `right`
                    escape the header without being cut off. */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
                    <div
                        className="absolute -top-16 -right-10 w-64 h-64 rounded-full opacity-30"
                        style={{ background: accent, filter: "blur(60px)" }}
                    />
                </div>
                <div className="relative">
                    <h1
                        className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight"
                        style={{
                            background: accent,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}
                        data-testid="page-title"
                    >
                        {title}
                    </h1>
                    {sub ? <p className="text-sm text-stone-500 mt-1.5 max-w-2xl" data-testid="page-sub">{sub}</p> : null}
                </div>
                <div className="flex items-center gap-2 flex-wrap relative">{right}</div>
            </div>
        </div>
    );
}
